import {
    Anchor,
    Button,
    Checkbox,
    Flex,
    Group,
    Input,
    Loader,
    MultiSelect,
    Progress,
    Space,
    Stack,
    Text,
    TextInput,
    Title
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm, yupResolver } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import {
    DayChipGroup,
    formatPhoneNumber,
    PageLoadIndicator,
    removeNullValues,
    showErrorNotification,
    showSuccessNotification,
    TIMES,
    titleCase,
    useCreatePublicWebsiteLeadMutation,
    useGetPublicInsuranceEntitiesQuery,
    useGetPublicInviteLandingEntitiesQuery,
    useGetPublicOrganizationDetailsQuery,
    useGetPublicTherapySubCategoryGroupsQuery
} from "@therapprove/common";
import type { PublicUnifiedPriorityEntryInput } from "@therapprove/common/src/graphql-public/graphql";
import { isSameDay } from "date-fns";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Dialog } from "../components/Dialog";
import {
    PublicPriorityEntryInitialValues,
    PublicPriorityEntrySchema
} from "./EmbeddedPriorityViewForm.validation";

export const EmbeddedPriorityViewForm = () => {
    // Get the 'organizationId' path parameter.
    const { organizationId } = useParams();
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Post dynamic height updates to the parent page (used by apps/landing/public/embed.js)
    // Must be declared before any early returns to avoid breaking the Rules of Hooks.
    useEffect(() => {
        const getHeight = () => {
            const el = containerRef.current;
            const measured = el
                ? Math.ceil(el.getBoundingClientRect().height)
                : 0;
            const docHeight = Math.max(
                document.body?.scrollHeight ?? 0,
                document.documentElement?.scrollHeight ?? 0,
                measured
            );
            return Math.max(1, docHeight);
        };

        const postHeight = (targetOrigin: string = "*") => {
            const height = getHeight();
            window.parent?.postMessage(
                { type: "therapprove:height", height },
                targetOrigin
            );
        };

        // Initial post + on resize/DOM changes
        const rafPost = () => requestAnimationFrame(() => postHeight("*"));
        rafPost();

        const ro =
            "ResizeObserver" in window
                ? new ResizeObserver(() => {
                      rafPost();
                  })
                : null;
        if (ro && containerRef.current) ro.observe(containerRef.current);

        const onWindowResize = () => rafPost();
        window.addEventListener("resize", onWindowResize);

        // Parent can request a height (embed.js does this on iframe load)
        const onMessage = (event: MessageEvent) => {
            const data =
                typeof event.data === "string"
                    ? (() => {
                          try {
                              return JSON.parse(event.data);
                          } catch {
                              return null;
                          }
                      })()
                    : event.data;
            if (data?.type === "therapprove:requestHeight") {
                postHeight(event.origin || "*");
            }
        };
        window.addEventListener("message", onMessage);

        return () => {
            ro?.disconnect();
            window.removeEventListener("resize", onWindowResize);
            window.removeEventListener("message", onMessage);
        };
    }, []);

    // State
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [
        areTermsAndPrivacyPolicyChecked,
        setAreTermsAndPrivacyPolicyChecked
    ] = useState(false);
    const [searchInsurances, setSearchInsurances] = useState("");

    const [debouncedSearchInsurances] = useDebouncedValue(
        searchInsurances,
        500
    );

    // Mutations
    const [{ fetching: creatingWebSiteLead }, createWebSiteLead] =
        useCreatePublicWebsiteLeadMutation();

    // Form Data
    const form = useForm<PublicUnifiedPriorityEntryInput>({
        mode: "controlled",
        initialValues: PublicPriorityEntryInitialValues,
        validate: yupResolver(PublicPriorityEntrySchema),
        validateInputOnChange: false
    });

    // Queries
    const [{ data, fetching, error }] = useGetPublicOrganizationDetailsQuery({
        variables: {
            organizationId: organizationId ?? ""
        },
        pause: !organizationId
    });

    const [{ data: entityData }] = useGetPublicInviteLandingEntitiesQuery({
        variables: {
            organizationId: organizationId ?? ""
        },
        pause: !organizationId
    });

    const [{ data: insuranceData, fetching: insuranceFetching }] =
        useGetPublicInsuranceEntitiesQuery({
            variables: {
                search: debouncedSearchInsurances
            },
            pause: !debouncedSearchInsurances
        });

    // Data
    const organizationName =
        data?.organizations?.publicOrganizationDetails?.name;
    const insurances = insuranceData?.insurances?.insurancesList;
    const isButtonDisabled = !areTermsAndPrivacyPolicyChecked;

    // Nested destructuring of the entity data.
    const conditions = entityData?.conditions?.conditionsList;
    const therapyCategories =
        entityData?.therapyCategories?.therapyCategoriesList;

    // Fetch the therapy sub category groups.
    const [{ data: therapySubCategoryGroups }] =
        useGetPublicTherapySubCategoryGroupsQuery({
            variables: {
                therapyCategoryIds:
                    form.values.patient?.therapyCategoryIds ?? []
            },
            pause: !form.values.patient?.therapyCategoryIds
        });

    // Destructure the therapy sub categories.
    const therapySubCategories =
        therapySubCategoryGroups?.therapySubCategories
            ?.therapySubCategoryGroups;

    const steps: { label: string; error?: string[] }[] = [
        {
            label: "Primary Contact",
            error: [
                "caregiver.firstName",
                "caregiver.lastName",
                "caregiver.email",
                "caregiver.phoneNumber"
            ]
        },
        {
            label: "About Your Child",
            error: [
                "patient.firstName",
                "patient.lastName",
                "patient.dateOfBirth",
                "patient.postalCode",
                "patient.therapyCategoryIds"
            ]
        },
        { label: "Almost Done - Let's Match You" }
    ];

    const getFirstStepWithErrors = (errors: Record<string, unknown>) => {
        for (let i = 0; i < steps.length; i++) {
            const stepErrors = steps[i]?.error ?? [];
            if (stepErrors.some((path) => !!errors[path])) return i;
        }
        return null;
    };

    const additionalStepHasIssues =
        hasAttemptedSubmit && !areTermsAndPrivacyPolicyChecked;

    const parseSuccessDestination = (raw: string | null | undefined) => {
        if (!raw) return null;
        try {
            // Allow same-origin redirects without hardcoding the domain
            if (raw.startsWith("/"))
                return new URL(raw, window.location.origin).toString();

            const u = new URL(raw);
            if (u.protocol !== "http:" && u.protocol !== "https:") return null;
            return u.toString();
        } catch {
            return null;
        }
    };

    // Redirect URL is controlled by the landing app (no client embed snippet changes needed).
    // Configure with VITE_EMBED_SUCCESS_URL (recommended). Query param `success_url` can override it.
    const getSuccessDestination = () => {
        const qs = new URLSearchParams(window.location.search).get(
            "success_url"
        );
        const envRaw =
            typeof import.meta.env.VITE_EMBED_SUCCESS_URL === "string"
                ? import.meta.env.VITE_EMBED_SUCCESS_URL
                : null;
        return (
            parseSuccessDestination(qs) ??
            parseSuccessDestination(envRaw) ??
            "https://www.therapprove.com/thanks-1"
        );
    };

    const stepHasIssues = (stepIndex: number) => {
        if (!hasAttemptedSubmit) return false;
        if (stepIndex === 2) return additionalStepHasIssues;
        const stepErrors = steps[stepIndex]?.error ?? [];
        return stepErrors.some((path) => !!form.errors[path]);
    };

    const submitPublicPriorityEntry = async (event: FormEvent) => {
        event.preventDefault();

        // Set the organization ID for the form.
        form.setFieldValue("organizationId", organizationId ?? "");

        const valid = form.validate();
        if (valid.hasErrors) {
            const firstErrorStep = getFirstStepWithErrors(valid.errors);
            if (firstErrorStep !== null) setActiveStep(firstErrorStep);
            return;
        }
        const formValues = form.getValues();

        removeNullValues(formValues);

        if (!areTermsAndPrivacyPolicyChecked) {
            showErrorNotification(
                "Please accept the terms and conditions and privacy policy."
            );
            return;
        } else {
            // If they have accepted the terms and conditions and privacy policy, set the value to true.
            form.setFieldValue("caregiver.agreeToTermsConditions", true);
        }
        if (!organizationId) {
            showErrorNotification(
                `This link is invalid, please contact ${organizationName} for a new invite link.`
            );
            return;
        }

        // Create the web site lead.
        const { data, error } = await createWebSiteLead({
            input: formValues
        });

        // If the web site lead is created successfully, show the success notification.
        if (data?.websiteLeads?.createWebsiteLead?.success) {
            // Notify the parent window (embed-core.js) so it can fire a GA4 conversion event.
            if (window.parent !== window) {
                window.parent.postMessage(
                    {
                        type: "therapprove:form_submitted",
                        formId: organizationId || undefined,
                        submissionId:
                            crypto.randomUUID?.() || Date.now().toString()
                    },
                    "*"
                );
            }

            const successUrl = getSuccessDestination();
            // Redirect should never be blocked by notifications/state resets.
            try {
                window.location.assign(successUrl);
            } finally {
                try {
                    showSuccessNotification(
                        `You're request has been submitted to ${organizationName}!`
                    );
                } catch {
                    // ignore (notifications provider may not be mounted on this route)
                }
                setAreTermsAndPrivacyPolicyChecked(false);
                setActiveStep(0);
                form.reset();
            }
            return;
        }

        // If there is an error, show the error notification.
        if (error || !data?.websiteLeads?.createWebsiteLead?.success) {
            showErrorNotification(
                error?.message ??
                    "An error occurred, please contact the organization for help.",
                3500
            );
        }
    };

    // Error state.
    if (error) {
        return (
            <Dialog
                title="Invalid Link"
                description=""
                failureMessage="Sorry, that link is invalid, please contact the organization to get another invite link, or contact support@therapprove.com if you believe this is an error."
                icon="undo"
                showAgreement={false}
                isSuccessful={false}
            />
        );
    }

    if (fetching) {
        return <PageLoadIndicator />;
    }

    return (
        <Stack
            ref={containerRef}
            w="100%"
            align="center"
            style={{
                maxWidth: "32rem",
                margin: "0 auto",
                padding: "1rem",
                height: "100%",
                maxHeight: "675px"
            }}
        >
            <Stack
                flex={1}
                mt="md"
                style={{
                    width: "100%",
                    flexGrow: 1
                }}
            >
                <Stack gap={8} mb="lg" style={{ width: "100%" }}>
                    <Flex
                        justify="space-between"
                        align="baseline"
                        wrap="nowrap"
                        gap="xs"
                    >
                        <Title
                            order={4}
                            style={{ color: "var(--mantine-color-dark-9)" }}
                        >
                            {steps[activeStep]?.label}
                        </Title>
                        <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                            Step {activeStep + 1} of {steps.length}
                        </Text>
                    </Flex>
                    <Progress
                        value={((activeStep + 1) / steps.length) * 100}
                        color="var(--mantine-color-primary-9)"
                        size="sm"
                        radius="xl"
                        style={{ flex: 1, minHeight: 8 }}
                    />
                </Stack>

                <Space h="xl" />

                {/* STEP 1: PRIMARY CONTACT */}
                {activeStep === 0 && (
                    <Stack gap="sm" align="left">
                        <TextInput
                            data-autofocus
                            required
                            size="md"
                            radius="md"
                            label="First Name"
                            placeholder="First Name"
                            {...form.getInputProps("caregiver.firstName")}
                            onChange={(event) => {
                                form.setFieldValue(
                                    "caregiver.firstName",
                                    titleCase(event.target.value)
                                );
                            }}
                        />
                        <TextInput
                            required
                            size="md"
                            radius="md"
                            label="Last Name"
                            placeholder="Last Name"
                            {...form.getInputProps("caregiver.lastName")}
                            onChange={(event) => {
                                form.setFieldValue(
                                    "caregiver.lastName",
                                    titleCase(event.target.value)
                                );
                            }}
                        />
                        <TextInput
                            required
                            label="Email"
                            placeholder="your.email@example.com"
                            size="md"
                            radius="md"
                            {...form.getInputProps("caregiver.email")}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                form.setFieldValue(
                                    "caregiver.email",
                                    event.target.value.trim().toLowerCase()
                                );
                            }}
                        />
                        <TextInput
                            required
                            label="Phone"
                            placeholder="(000) 000-0000"
                            size="md"
                            radius="md"
                            {...form.getInputProps("caregiver.phoneNumber")}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                const formattedNumber = formatPhoneNumber(
                                    event.target.value.trim()
                                );
                                form.setFieldValue(
                                    "caregiver.phoneNumber",
                                    formattedNumber
                                );
                            }}
                        />
                        <TextInput
                            required
                            label="Zip Code"
                            placeholder="We match you with nearby providers"
                            size="md"
                            radius="md"
                            maxLength={5}
                            {...form.getInputProps("patient.postalCode")}
                        />
                    </Stack>
                )}

                {/* STEP 2: ABOUT YOUR CHILD */}
                {activeStep === 1 && (
                    <Stack gap="sm">
                        <TextInput
                            required
                            label="First Name"
                            placeholder="First Name"
                            size="md"
                            radius="md"
                            {...form.getInputProps("patient.firstName")}
                            onChange={(event) => {
                                form.setFieldValue(
                                    "patient.firstName",
                                    titleCase(event.target.value)
                                );
                            }}
                        />
                        <TextInput
                            required
                            label="Last Name"
                            placeholder="Last Name"
                            size="md"
                            radius="md"
                            {...form.getInputProps("patient.lastName")}
                            onChange={(event) => {
                                form.setFieldValue(
                                    "patient.lastName",
                                    titleCase(event.target.value)
                                );
                            }}
                        />
                        <DateInput
                            required
                            allowDeselect
                            firstDayOfWeek={0}
                            label="Date of Birth"
                            placeholder="Date of Birth"
                            valueFormat="MMM DD, YYYY"
                            size="md"
                            radius="md"
                            defaultDate={new Date()}
                            clearable
                            {...form.getInputProps("patient.dateOfBirth")}
                            getDayProps={(date) => {
                                return {
                                    style: {
                                        backgroundColor: isSameDay(
                                            date,
                                            new Date()
                                        )
                                            ? "var(--mantine-color-primary-7)"
                                            : "inherit"
                                    }
                                };
                            }}
                        />
                        <MultiSelect
                            required
                            variant="pill"
                            label="Therapies Needed"
                            placeholder="Select the services your child needs"
                            size="md"
                            radius="md"
                            searchable
                            clearable
                            data={therapyCategories}
                            {...form.getInputProps(
                                "patient.therapyCategoryIds"
                            )}
                            onChange={(value) => {
                                form.setFieldValue(
                                    "patient.therapyCategoryIds",
                                    value
                                );
                                form.setFieldValue(
                                    "patient.therapySubCategoryIds",
                                    []
                                );
                            }}
                        />
                        <MultiSelect
                            variant="pill"
                            label="Skills / Interventions Needed"
                            placeholder="Skills or support your child needs"
                            size="md"
                            radius="md"
                            searchable
                            clearable
                            rightSection={
                                insuranceFetching ? <Loader size={16} /> : null
                            }
                            disabled={
                                !form.values.patient?.therapyCategoryIds?.length
                            }
                            data={therapySubCategories ?? []}
                            {...form.getInputProps(
                                "patient.therapySubCategoryIds"
                            )}
                        />
                    </Stack>
                )}

                {/* STEP 3: ADDITIONAL */}
                {activeStep === 2 && (
                    <Stack gap="sm">
                        <Stack gap="sm" mt="md" mb="md">
                            <Input.Label size="md">
                                When Are You Available?
                            </Input.Label>
                            <DayChipGroup
                                abbreviate
                                size="md"
                                value={form.values.patient?.daysAvailable?.map(
                                    (d) => titleCase(d)
                                )}
                                onChipChange={(v: string[]) =>
                                    form.setFieldValue(
                                        "patient.daysAvailable",
                                        v
                                    )
                                }
                            />
                        </Stack>
                        <MultiSelect
                            variant="pill"
                            label="Time Available"
                            placeholder="Time Available"
                            size="md"
                            radius="md"
                            clearable
                            searchable
                            checkIconPosition="right"
                            data={TIMES}
                            {...form.getInputProps("patient.timeAvailable")}
                        />
                        <MultiSelect
                            variant="pill"
                            label="Insurances"
                            placeholder="We prioritize providers that are in-network"
                            size="md"
                            radius="md"
                            limit={100}
                            searchable
                            clearable
                            dropdownOpened={searchInsurances.length > 1}
                            data={insurances}
                            searchValue={searchInsurances}
                            onSearchChange={(value) => {
                                setSearchInsurances(value);
                            }}
                            {...form.getInputProps("patient.insuranceIds")}
                        />
                        <MultiSelect
                            variant="pill"
                            label="Diagnoses/Conditions"
                            placeholder="Select any that apply, not sure is ok"
                            size="md"
                            radius="md"
                            searchable
                            clearable
                            data={conditions}
                            {...form.getInputProps("patient.conditionIds")}
                        />
                        <Checkbox
                            label={
                                <>
                                    I accept the{" "}
                                    <Anchor
                                        href="https://therapprove.com/terms/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            fontSize: "14px",
                                            textDecoration: "underline"
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        terms and conditions
                                    </Anchor>{" "}
                                    and{" "}
                                    <Anchor
                                        href="https://therapprove.com/privacy-policy/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            fontSize: "14px",
                                            textDecoration: "underline"
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        privacy policy
                                    </Anchor>
                                </>
                            }
                            color="var(--mantine-color-primary-9)"
                            size="sm"
                            mt="md"
                            checked={areTermsAndPrivacyPolicyChecked}
                            onChange={(event) => {
                                setAreTermsAndPrivacyPolicyChecked(
                                    event.currentTarget.checked
                                );

                                if (event.currentTarget.checked) {
                                    form.setFieldValue(
                                        "inquiredAt",
                                        new Date().toISOString()
                                    );
                                }
                            }}
                        />
                    </Stack>
                )}

                {/* FOOTER */}
                <Stack mt="xl">
                    <Group
                        justify="space-between"
                        align="center"
                        style={{
                            width: "100%"
                        }}
                    >
                        {activeStep > 0 ? (
                            <Button
                                variant="outline"
                                size="md"
                                style={{ width: "45%", borderRadius: "5rem" }}
                                onClick={() =>
                                    setActiveStep((s) => Math.max(0, s - 1))
                                }
                            >
                                Previous
                            </Button>
                        ) : (
                            <div />
                        )}

                        {activeStep < steps.length - 1 ? (
                            <Button
                                size="md"
                                style={{ width: "45%", borderRadius: "5rem" }}
                                onClick={() => setActiveStep(activeStep + 1)}
                            >
                                Next Step
                            </Button>
                        ) : (
                            <Button
                                size="md"
                                style={{ width: "45%", borderRadius: "5rem" }}
                                disabled={
                                    isButtonDisabled || creatingWebSiteLead
                                }
                                loading={creatingWebSiteLead}
                                onClick={submitPublicPriorityEntry}
                            >
                                Get Matched
                            </Button>
                        )}
                    </Group>
                </Stack>
            </Stack>
        </Stack>
    );
};
